import { HttpErrorUtils, HttpResponseErrorBoltServer } from '@bolteu/http-client-bolt-server';
import type { Nullable } from '@provider-portal/common/types/types';

import { getCurrentDayStart } from 'utils/date';

import {
  MAX_PROMOTION_PERIOD_DAYS,
  MAX_PROMOTION_START_IN_DAYS,
  MIN_PROMOTION_PERIOD_DAYS,
} from './const';

const oneDayMs = 24 * 3600 * 1000;

interface PromotionDates {
  startDate: number;
  endDate: number;
}

export interface MinMaxDate {
  minDate: number;
  maxDate: number;
}

export const getStartRestrictions = ({ startDate, endDate }: PromotionDates): MinMaxDate => {
  // Deduct 1 sec to prohibit additional day promotion
  const maxPromotionStartInMs = MAX_PROMOTION_START_IN_DAYS * oneDayMs - 1000;
  const start = getCurrentDayStart().getTime();

  return {
    minDate: Math.max(start, startDate),
    maxDate: Math.min(start + maxPromotionStartInMs, endDate),
  };
};

export const getEndRestrictions = (
  { endDate }: PromotionDates,
  { minDate, maxDate }: MinMaxDate,
  start: Nullable<number>,
): MinMaxDate => {
  // Deduct 1 min to allow select 1 day promotion (23:59:00 period)
  const minPromotionPeriodMs = MIN_PROMOTION_PERIOD_DAYS * oneDayMs - 60000;
  // Deduct 1 min to prohibit additional day promotion
  const maxPromotionPeriodMs = MAX_PROMOTION_PERIOD_DAYS * oneDayMs - 60000;

  return {
    minDate: Math.min(Math.max(minDate, start ?? minDate) + minPromotionPeriodMs, endDate),
    maxDate: Math.min(Math.min(maxDate, start ?? maxDate) + maxPromotionPeriodMs, endDate),
  };
};

export function isNullable<T>(input: T): boolean {
  return input === null || input === undefined;
}

export function isNonNullable<T>(input: T): input is NonNullable<T> {
  return !isNullable(input);
}

export function fetchCode<E extends Error>(error: E): number | null {
  if (HttpErrorUtils.isInstanceOf(error, HttpResponseErrorBoltServer.ServerResponseError)) {
    return error.response.code;
  }
  const code = Number((error.message ?? '').split(' ')[0]);
  return Number.isNaN(code) ? null : code;
}
